// purchaseTaxInvoice.js — 매입세금계산서(전자세금계산서 목록, 구분="매입") 엑셀을 업로드해
// "실제 매입" 데이터를 반영하는 모듈. ecountSales.js(매출)와 동일한 형식의 엑셀(홈택스 전자세금계산서
// 목록 다운로드)을 그대로 재사용하되, 구분="매입" 행을 뽑아 공급자(나에게 세금계산서를 발행한 거래처)
// 기준으로 집계한다. price-manager 프로젝트의 'purchaseDaily' 컬렉션에 저장(ecountSales.js와 같은
// Firebase 앱을 재사용 — priceManagerApp/ensureSignedIn을 그쪽에서 export해 가져다 쓴다).
import { getFirestore, collection, getDocs, query, where, orderBy, doc, writeBatch } from "firebase/firestore";
import { priceManagerApp, ensureSignedIn, rangeBounds } from "./ecountSales";

const db = () => getFirestore(priceManagerApp());
const norm = (s) => String(s ?? "").replace(/\s/g, "");

// 전자세금계산서 목록 엑셀에서 구분="매입" 행만 추출 → [{date, vendorCode, vendorName, amount, qty:0}]
// 매입은 "공급자"(나에게 발행한 거래처)가 상대방이므로 공급자상호/공급자사업자번호 열을 우선 사용한다.
export function parsePurchaseExcelRows(rows) {
  for (let i = 0; i < Math.min(rows.length, 12); i++) {
    const h = (rows[i] || []).map(norm);
    const gi = h.findIndex((c) => c === "구분");
    const vi = (() => {
      const a = h.findIndex((c) => c.includes("공급자상호"));
      if (a >= 0) return a;
      return h.findIndex((c) => c.includes("상호") && !c.includes("공급받는자"));
    })();
    const ai = h.findIndex((c) => c === "공급가액");
    const di = h.findIndex((c) => c === "일자" || c === "작성일자");
    if (gi >= 0 && vi >= 0 && ai >= 0 && di >= 0) {
      const ci = h.findIndex((c) => c.includes("공급자사업자번호") || (c.includes("사업자번호") && !c.includes("공급받는자")));
      const records = [];
      for (let r = i + 1; r < rows.length; r++) {
        const row = rows[r] || [];
        if (String(row[gi] ?? "").trim() !== "매입") continue; // 매출·빈 행 제외
        const dateDigits = String(row[di] ?? "").replace(/[^0-9]/g, "").slice(0, 8);
        if (dateDigits.length !== 8) continue;
        const date = `${dateDigits.slice(0, 4)}-${dateDigits.slice(4, 6)}-${dateDigits.slice(6, 8)}`;
        const vendorName = String(row[vi] ?? "").trim();
        const amount = Number(String(row[ai] ?? "").replace(/,/g, ""));
        if (!vendorName || !Number.isFinite(amount) || amount === 0) continue;
        records.push({ date, vendorCode: String(ci >= 0 ? row[ci] ?? "" : "").trim(), vendorName, amount, qty: 0 });
      }
      return { error: records.length ? "" : "'매입' 구분의 계산서 행을 찾지 못했습니다.", records };
    }
  }
  return { error: "헤더 행을 찾지 못했습니다. 전자세금계산서 목록(구분/공급자상호/공급가액/일자) 형식의 엑셀인지 확인해주세요.", records: [] };
}

// 파싱된 레코드를 업체·일자별로 합산해 purchaseDaily에 저장 (같은 업체·일자는 덮어씀 → 재업로드 안전).
export async function savePurchaseRecords(records) {
  await ensureSignedIn();
  const map = {};
  records.forEach((r) => {
    const key = `${r.date}_${r.vendorCode || r.vendorName}`;
    if (!map[key]) map[key] = { date: r.date, vendorCode: r.vendorCode, vendorName: r.vendorName, amount: 0, qty: 0, lines: 0 };
    map[key].amount += r.amount;
    map[key].qty += r.qty;
    map[key].lines += 1;
  });
  const aggregated = Object.values(map);
  const database = db();
  let batch = writeBatch(database);
  let n = 0;
  for (const a of aggregated) {
    const id = `${a.date}_${(a.vendorCode || a.vendorName).replace(/[/\s#?%[\]]/g, "_")}`;
    batch.set(doc(database, "purchaseDaily", id), { ...a, updatedAt: new Date(), source: "excel" }, { merge: true });
    n += 1;
    if (n % 400 === 0) { await batch.commit(); batch = writeBatch(database); }
  }
  if (n % 400 !== 0) await batch.commit();
  const dates = aggregated.map((a) => a.date).sort();
  return { saved: aggregated.length, vendors: new Set(aggregated.map((a) => a.vendorName)).size, from: dates[0], to: dates[dates.length - 1] };
}

// 최근 days일(0=전체, "ytd"=올해 1월 1일부터)의 실제 매입 레코드를 반환.
export async function fetchPurchaseRecords(days = 30) {
  await ensureSignedIn();
  const col = collection(db(), "purchaseDaily");
  // 기간 해석은 매출과 동일한 규칙(ecountSales.rangeBounds)을 쓴다 — 분기·반기도 그대로 지원된다.
  const { from, to } = rangeBounds(days);
  const conds = [];
  if (from) conds.push(where("date", ">=", from));
  if (to) conds.push(where("date", "<=", to));
  const q = query(col, ...conds, orderBy("date", "desc"));
  const snap = await getDocs(q);
  const out = [];
  snap.docs.forEach((d) => {
    const x = d.data();
    const date = String(x.date || "").slice(0, 10);
    const amount = Number(x.amount);
    if (!date || !Number.isFinite(amount)) return;
    out.push({
      date,
      vendorCode: String(x.vendorCode || "").trim(),
      vendorName: String(x.vendorName || "").trim() || "(거래처 미지정)",
      amount,
      qty: Number(x.qty) || 0,
      updatedAt: x.updatedAt?.toDate ? x.updatedAt.toDate() : (x.updatedAt instanceof Date ? x.updatedAt : null),
    });
  });
  return out;
}
